import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { withTheme } from 'styled-components';

import ErrorDisplay from 'components/ErrorDisplay';
import FileNumber from './OrderFileNumber';
import OrderDocument from './OrderDocument';
import OrderTransactions from './OrderTransactions';
import Dialog from 'components/Dialog';
import Card from 'components/common/Card';
import LoadingSpinner from 'components/LoadingSpinner';
import CertificateItem from 'routes/OrderCreate/CertificateItem';
import {
  Bold,
  Text,
  ETANote,
  FlexItem,
  CardText,
  CardTitle,
  OrderCard,
  SummaryItem,
  AddressText,
  SummaryLabel,
  CardContainer,
  LoadingContain,
  CardFlexContain,
  CardMarginBottom,
  AssessmentNumber,
  StatusChipStyled,
  Button,
  Flex,
  FlexEnd,
  ButtonLabel
} from './Styled';

import {
  getText,
  getColor,
  isInvalid,
  getAddress,
  isComplete,
  getCt,
  getPlanParcel,
  formatDate,
  formatDateTime
} from 'utils';

import { CREDIT_CARD_SURCHARGE, SERVICE_FEE } from 'constants/orders';

const StatusChip = ({ color, text }) => (
  <StatusChipStyled color={color}>{text}</StatusChipStyled>
);

export const Order = ({
  theme,
  order,
  loading,
  is_postpaid,
  updateOrder,
  inputErrors,
  renderUpload,
  orderUpdated,
  errorMessage,
  requestUpdate,
  updateNewOrder,
  loadingOrderUpdate
}) => {
  const council = (order && order.council) || {};
  const user = (order && order.user) || {};
  
  const planParcel = getPlanParcel(order);
  const [ ct, pp]  = getCt(order);

  const history = useHistory();

  const lastUpdatedRef = useRef(orderUpdated);

  const [isOpen, setOpen] = useState(false);
  const [focussedItem, setFocussedItem] = useState(false);
  const [isOpenNewOrder, setOpenNewOrder] = useState(false);

  useEffect(
    function closeUpdateDialog() {
      if (lastUpdatedRef.current !== orderUpdated) {
        lastUpdatedRef.current = orderUpdated;
        setOpen(false);
      }
    },
    [ orderUpdated ]
  )

  const handleOpenDialog = (item) => {
    setFocussedItem(item);
    setOpen(true);
  };

  function requestCertificate(id) {
    requestUpdate(order.id, id);
  }

  const onOrderUpdate = () => {
    const address = {
      address_lot:       order.address_lot || '',
      address_unit:      order.address_unit || '',
      address_street_no: order.address_street_no || '',
      address_street_1:  order.address_street_1 || '',
      address_street_2:  order.address_street_2 || '',
      address_suburb:    order.address_suburb || '',
      address_state:     order.address_state || '',
      address_postcode:  order.address_postcode || ''
    };

    const meta = {
      address_ct: ct,
      address_plan_lot_section: pp,
      globalx_address_display: getAddress(order)
    }

    const council = {
      selected_council:    order.council.lga,
      selected_council_id: order.council_id
    }

    const title = {
      uploaded_title: true,
      title_order_id: order.id
    }

    updateNewOrder({
      ...address,
      ...meta,
      ...council,
      ...title,
      createFrom: { id: order.id, itemType: 'SEC187' }
    });

    history.push({ pathname: '/orders/council' });
  };

  const renderItems = () =>
    order.items &&
    order.items
      .filter((i) => i.name !== SERVICE_FEE && i.name !== CREDIT_CARD_SURCHARGE)
      .map((item) => {
        var showButton = false;
        var updatePending = (item.id === order.update_item_id);

        if (item.update_document_types) {        
          item.document_types.forEach(item1 => {
            if (item.update_document_types.some(item2 => item1 === item2)) {
              showButton = true;
            }
          });
        }

        if (showButton && order.is_completed) { 
            return (
              <FlexEnd>
                <CertificateItem renderAction={() => {
                  return (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <Button onClick={() => (!!item.update_available && item.days_remaining > 0) ? handleOpenDialog(item) : setOpenNewOrder(true)} secondary>
                        <ButtonLabel>Request Update</ButtonLabel>
                      </Button>
                    </div>
                  )
                }} removePadding summary="true" key={item.id} item={item} />
              </FlexEnd>
            )
        } else if (updatePending && !order.is_completed && order.status !== 'INVALID' && item.days_remaining > 0 && !order.invalid_reason) {
            return (
              <FlexEnd>
                <CertificateItem renderAction={() => {
                  return (
                    <StatusChip
                      text={getText("UPDATE PENDING")}
                      color={getColor("UPDATE PENDING")}
                    />
                  )
                }} removePadding summary="true" key={item.id} item={item} />
              </FlexEnd>
            )
        }

        else if (updatePending && !order.is_completed && order.status !== 'INVALID' && item.days_remaining > 0 && !!order.invalid_reason) {
          return (
            <FlexEnd>
              <CertificateItem renderAction={() => {
                return (
                  <StatusChip
                    text={getText("UPDATE INVALIDATED")}
                    color={getColor("UPDATE INVALIDATED")}
                  />
                )
              }} removePadding summary="true" key={item.id} item={item} />
            </FlexEnd>
          )
        }

        return (
          <CertificateItem removePadding summary="true" key={item.id} item={item} />
        )
    });

  const renderDocuments = () =>
    order.documents && order.documents.length ? (
      order.documents.map((orderDocument) => (
        <OrderDocument
          key={orderDocument.id}
          {...orderDocument}
          orderId={order.id}
        />
      ))
    ) : (
      <CardText>No Documents have been uploaded</CardText>
    );
  if (!order) return null;

  if (loading)
    return (
      <LoadingContain>
        <LoadingSpinner large />
        Loading Council Search...
      </LoadingContain>
    );

  return (
    <React.Fragment>
      <OrderCard>
        <SummaryItem>
          <SummaryLabel>Order Address</SummaryLabel>
          <AddressText>{getAddress(order) || '...'}{planParcel && <span>{' — '}{planParcel}</span>}</AddressText>
        </SummaryItem>
      </OrderCard>


      <Dialog
        open={isOpen}
        hideActions
        onClose={() => setOpen(false)}
        title=""
      >
        {
          loadingOrderUpdate ? 
          (
            <LoadingContain>
              <LoadingSpinner large />
              Requesting update...
            </LoadingContain>
          )
        :
        <>
          <h1 style={{ marginTop: '0px', color: '#18242a', fontSize: '1.2em', fontWeight: 500 }}>Request Update - <span style={{ fontWeight: 400 }}>Section 187</span></h1>
          <p style={{ marginTop: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0rem', padding: '0.5rem', fontWeight: 400, marginBottom: '0px' }}>Days Remaining: {focussedItem.days_remaining} days</p>
          <p style={{ marginBottom: '1.5rem', marginTop: '0px', border: '1px', borderStyle: 'solid', borderColor: '#f3f4f6', padding: '1rem', fontSize: '0.875em', fontWeight: 400, lineHeight: 1.5, color: '#333' }}>
            You can request an updated certificate as many times as you like within {focussedItem.update_days} days of the order creation date at no cost.
            Would you like to request an updated certificate?
          </p>
  
          <ErrorDisplay message={errorMessage} />

          <Flex>
            <Button secondary onClick={() => setOpen(false)}>
              <ButtonLabel>Cancel</ButtonLabel>
            </Button>

            <FlexEnd>
              <Button onClick={() => requestCertificate(focussedItem.id)}>
                <ButtonLabel>Yes, Order Now</ButtonLabel>
              </Button>
            </FlexEnd>
          </Flex>
        </>
        }
      </Dialog>

      <Dialog
        open={isOpenNewOrder}
        hideActions
        showFloatingCancel
        onClose={() => setOpenNewOrder(false)}
        title=""
      >
        <h1 style={{ marginTop: '0px', color: '#18242a', fontSize: '1.2em', fontWeight: 500 }}>Request Update - <span style={{ fontWeight: 400 }}>Section 187</span></h1>
        <p style={{ marginTop: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0rem', padding: '0.5rem', fontWeight: 400, marginBottom: '0px' }}>Days Remaining: None</p>
        <p style={{ marginBottom: '1.5rem', marginTop: '0px', border: '1px', borderStyle: 'solid', borderColor: '#f3f4f6', padding: '1rem', fontSize: '0.875em', fontWeight: 400, lineHeight: 1.5, color: '#333' }}>The timeframe to request an updated certificate at no cost has expired. Would you like to order a new certificate?</p>

        <Flex>
          <Button secondary onClick={() => setOpenNewOrder(false)}>
            <ButtonLabel>Cancel</ButtonLabel>
          </Button>

          <FlexEnd>
            <Button onClick={onOrderUpdate}>
              <ButtonLabel>Start Order</ButtonLabel>
            </Button>
          </FlexEnd>
        </Flex>
      </Dialog>

      <CardMarginBottom>
        <CardContainer>
          <CardFlexContain>
            <FlexItem>
              <CardTitle>Details</CardTitle>

              <SummaryItem>
                <SummaryLabel>Order Status</SummaryLabel>
                <StatusChip
                  text={getText(order.status)}
                  color={getColor(order.status)}
                />
              </SummaryItem>

              {order.assessment_number && (
                <SummaryItem>
                  <SummaryLabel>Assessment Number</SummaryLabel>
                  <AssessmentNumber>{order.assessment_number}</AssessmentNumber>
                </SummaryItem>
              )}

              {
                !isInvalid(order.status) && !isComplete(order.status) &&
                <React.Fragment>
                  <CardText>
                    <Bold>Estimated Time Of Arrival</Bold>
                    {!!order.eta ? formatDate(order.eta) : 'N/A'}
                  </CardText>
                  {!!order.eta_message && <ETANote>{order.eta_message}</ETANote>}
                </React.Fragment>
              }

              <CardText>
                <Bold>Last updated</Bold>
                {formatDateTime(order.updated_on)}
              </CardText>

              <CardText>
                <Bold>Created on</Bold>
                {formatDateTime(order.created_on)}
              </CardText>

              {isInvalid(order.status) && (
                <React.Fragment>
                  <Text color={theme.dangerColor}>Invalidation Reason</Text>
                  <CardText>{order.invalid_reason || ''}</CardText>
                </React.Fragment>
              )}

              <FileNumber
                updateOrder={updateOrder}
                inputErrors={inputErrors}
                orderUpdated={orderUpdated}
                loading={loadingOrderUpdate}
                fileNumber={order.file_number}
              />

              {
                order.additional_information &&
                <CardText>
                  <div><Bold>Additional Information</Bold></div>
                  <div>{order.additional_information}</div>
                </CardText>
              }

              {!!order.strata &&
                <CardText>
                  <Bold>Strata</Bold>
                  {order.strata_name || order.strata.name}
                </CardText>
              }

              {
                !!order.proposed_settlement_date &&
                <CardText>
                  <div><Bold>Proposed Settlement Date</Bold></div>
                  <div style={{ marginTop: '0.5rem'}}>{order.proposed_settlement_date}</div>
                </CardText>
              }
            </FlexItem>


            <FlexItem>
              <CardTitle>Applicant</CardTitle>
              <CardText>
                <Bold>Full Name</Bold>
                {`${user.first_name} ${user.last_name}`}
              </CardText>
              <CardText>
                <Bold>Mobile Number</Bold>
                {`${user.mobile_number}`}
              </CardText>
              <CardText>
                <Bold>Email Address</Bold>
                {`${user.email_address}`}
              </CardText>
            </FlexItem>
          </CardFlexContain>
        </CardContainer>
      </CardMarginBottom>

      {
        order.transactions && !!order.transactions.length &&
        <CardMarginBottom>
          <CardContainer>
            <CardTitle>Transactions</CardTitle>
            <OrderTransactions
              is_postpaid={is_postpaid}
              orderId={order.id}
              items={order.transactions}
              orderComplete={isComplete(order.status)}
            />
          </CardContainer>
        </CardMarginBottom>
      }

      <CardMarginBottom>
        <CardContainer>
          <CardFlexContain>
            {!!order.council && <FlexItem>
              <CardTitle>Council</CardTitle>
              <CardText>
                <Bold>Name</Bold>
                {council.name || ''}
              </CardText>

              <CardText>
                <Bold>Address</Bold>
                {getAddress(council) || '...'}
              </CardText>

              <CardText>
                <Bold>Email Address</Bold>
                {council.email || ''}
              </CardText>

              <CardText>
                <Bold>Phone</Bold>
                {council.phone || ''}
              </CardText>

              <CardText>
                <Bold>Fax</Bold>
                {council.fax || ''}
              </CardText>
            </FlexItem>}
            <FlexItem>
              <CardTitle>Order</CardTitle>

              {renderItems()}
            </FlexItem>
          </CardFlexContain>
        </CardContainer>
      </CardMarginBottom>

      <Card.Content>
        <CardTitle>Documents</CardTitle>
        {renderDocuments()}
        {renderUpload()}
      </Card.Content>
    </React.Fragment>
  );
};

export default withTheme(Order);
